<script lang="ts">
    let openSection = $state<string | null>('basics');

    function toggle(id: string) {
        openSection = openSection === id ? null : id;
    }
</script>

<div class="guide">
    <div class="guide-header">
        <h3>Гайд для початківців</h3>
        <p class="text-muted">Як читати графік, патерни та індикатори — від простого до складного</p>
    </div>

    <!-- 1. З чого починати -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'basics'} onclick={() => toggle('basics')}>
            <span class="step-badge">1</span>
            З чого починати новачку
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'basics'}
        <div class="guide-body">
            <p>Торгівля ф'ючерсами — це не казино. Ваш головний інструмент — <strong>терпіння та система</strong>. Дотримуйтесь такого порядку дій:</p>

            <ol class="steps-list">
                <li>
                    <strong>Визначте тренд</strong> — дивіться на <em>денний (1d)</em> або <em>4-годинний (4h)</em> графік. Якщо ціна йде вниз — не відкривайте лонги.
                </li>
                <li>
                    <strong>Перевірте RSI</strong> — якщо RSI &gt; 70 ринок перекуплений (небезпечно відкривати лонг), якщо &lt; 30 — перепроданий (небезпечно відкривати шорт).
                </li>
                <li>
                    <strong>Знайдіть патерн</strong> — шукайте один чіткий сигнал розвороту або продовження.
                </li>
                <li>
                    <strong>Перевірте Funding Rate</strong> — екстремальні значення попереджають про перегрітий ринок.
                </li>
                <li>
                    <strong>Відкривайте позицію тільки коли кілька сигналів збігаються</strong> — один патерн не є приводом для входу.
                </li>
            </ol>

            <div class="tip tip-green">
                💡 <strong>Правило новачка:</strong> перші 2 тижні тільки спостерігайте. Відкривайте Paper Trading (без реальних грошей), щоб перевірити свою стратегію.
            </div>
        </div>
        {/if}
    </div>

    <!-- 2. Таймфрейми -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'timeframes'} onclick={() => toggle('timeframes')}>
            <span class="step-badge">2</span>
            Які таймфрейми і коли використовувати
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'timeframes'}
        <div class="guide-body">
            <div class="tf-grid">
                <div class="tf-card tf-macro">
                    <div class="tf-label">Макро (контекст)</div>
                    <div class="tf-values">1w · 3d · 1d</div>
                    <div class="tf-desc">Загальний напрямок ринку. Визначте: бичачий чи ведмежий тренд? Де ключові рівні підтримки/опору? <strong>Завжди починайте звідси.</strong></div>
                </div>
                <div class="tf-card tf-mid">
                    <div class="tf-label">Середній (вхід)</div>
                    <div class="tf-values">4h · 2h · 1h</div>
                    <div class="tf-desc">Пошук патернів для входу. Підтверджуйте сигнали з денного ТФ. Більшість торгових рішень приймається тут.</div>
                </div>
                <div class="tf-card tf-micro">
                    <div class="tf-label">Мікро (точка входу)</div>
                    <div class="tf-values">30m · 15m · 5m</div>
                    <div class="tf-desc">Уточнення точки входу після знайденого патерну на 4h/1h. <strong>Ніколи не торгуйте тільки з 5m</strong> — занадто багато шуму.</div>
                </div>
            </div>

            <div class="tip tip-blue">
                📌 <strong>Multi-timeframe аналіз:</strong> 1d показує тренд → 4h показує патерн → 15m показує точку входу.
            </div>
        </div>
        {/if}
    </div>

    <!-- 3. Свічкові патерни -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'candles'} onclick={() => toggle('candles')}>
            <span class="step-badge">3</span>
            Свічкові патерни — що, де і коли
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'candles'}
        <div class="guide-body">
            <p>Свічкові патерни — це сигнали, що формуються з 1–3 свічок. Самі по собі слабкі, але в поєднанні з рівнями та індикаторами — потужні.</p>

            <h6 class="mt-3">🟢 Починайте з цих (прості та надійні)</h6>
            <div class="pattern-table">
                <div class="pt-row header">
                    <span>Патерн</span><span>Сигнал</span><span>Де шукати</span><span>Надійність</span>
                </div>
                <div class="pt-row">
                    <span><strong>Hammer</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Після спаду, на підтримці</span>
                    <span>⭐⭐⭐</span>
                </div>
                <div class="pt-row">
                    <span><strong>Shooting Star</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Після зросту, на опорі</span>
                    <span>⭐⭐⭐</span>
                </div>
                <div class="pt-row">
                    <span><strong>Bullish Engulfing</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Дно тренду, на підтримці</span>
                    <span>⭐⭐⭐⭐</span>
                </div>
                <div class="pt-row">
                    <span><strong>Bearish Engulfing</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Топ тренду, на опорі</span>
                    <span>⭐⭐⭐⭐</span>
                </div>
                <div class="pt-row">
                    <span><strong>Doji</strong></span>
                    <span class="neutral">Невизначеність</span>
                    <span>На рівнях — попередження</span>
                    <span>⭐⭐</span>
                </div>
            </div>

            <h6 class="mt-3">🟡 Середній рівень (3 свічки)</h6>
            <div class="pattern-table">
                <div class="pt-row header">
                    <span>Патерн</span><span>Сигнал</span><span>Особливість</span><span>Надійність</span>
                </div>
                <div class="pt-row">
                    <span><strong>Morning Star</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Сильний розворот після падіння</span>
                    <span>⭐⭐⭐⭐</span>
                </div>
                <div class="pt-row">
                    <span><strong>Evening Star</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Сильний розворот після зросту</span>
                    <span>⭐⭐⭐⭐</span>
                </div>
                <div class="pt-row">
                    <span><strong>Three White Soldiers</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Підтвердження бичачого тренду</span>
                    <span>⭐⭐⭐⭐</span>
                </div>
                <div class="pt-row">
                    <span><strong>Three Black Crows</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Підтвердження ведмежого тренду</span>
                    <span>⭐⭐⭐⭐</span>
                </div>
                <div class="pt-row">
                    <span><strong>Harami (bull/bear)</strong></span>
                    <span class="neutral">Обидва</span>
                    <span>Слабкіший сигнал, потребує підтвердження</span>
                    <span>⭐⭐</span>
                </div>
            </div>

            <div class="tip tip-yellow">
                ⚠️ <strong>Важливо:</strong> Свічковий патерн — це лише <em>перший сигнал</em>. Входьте тільки якщо він знаходиться на ключовому рівні (підтримка/опір) і підтверджений індикатором.
            </div>
        </div>
        {/if}
    </div>

    <!-- 4. Графічні патерни -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'chartpatterns'} onclick={() => toggle('chartpatterns')}>
            <span class="step-badge">4</span>
            Графічні патерни — великі рухи ринку
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'chartpatterns'}
        <div class="guide-body">
            <p>Графічні патерни формуються на <strong>більших часових проміжках</strong> (10–50+ свічок) і дають значно більший потенціал руху. Це вже середній-просунутий рівень.</p>

            <h6 class="mt-3">🔵 Розворотні патерни (зміна тренду)</h6>
            <div class="pattern-table">
                <div class="pt-row header">
                    <span>Патерн</span><span>Сигнал</span><span>Потенціал руху</span><span>Складність</span>
                </div>
                <div class="pt-row">
                    <span><strong>Head & Shoulders</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Висота голови від лінії шиї</span>
                    <span>Середня</span>
                </div>
                <div class="pt-row">
                    <span><strong>Inverse H&S</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Висота голови від лінії шиї</span>
                    <span>Середня</span>
                </div>
                <div class="pt-row">
                    <span><strong>Cup & Handle</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Глибина чаші</span>
                    <span>Середня</span>
                </div>
                <div class="pt-row">
                    <span><strong>Double Top</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Висота між піком і нек-лайном</span>
                    <span>Низька</span>
                </div>
                <div class="pt-row">
                    <span><strong>Double Bottom</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Висота між дном і нек-лайном</span>
                    <span>Низька</span>
                </div>
            </div>

            <h6 class="mt-3">🟣 Патерни продовження (тренд продовжується)</h6>
            <div class="pattern-table">
                <div class="pt-row header">
                    <span>Патерн</span><span>Сигнал</span><span>Вхід</span><span>Особливість</span>
                </div>
                <div class="pt-row">
                    <span><strong>Bull Flag</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Пробій верхньої межі флагу</span>
                    <span>Після різкого зросту (флагшток)</span>
                </div>
                <div class="pt-row">
                    <span><strong>Bear Flag</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Пробій нижньої межі флагу</span>
                    <span>Після різкого падіння</span>
                </div>
                <div class="pt-row">
                    <span><strong>Bull Pennant</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Пробій вгору трикутника</span>
                    <span>Схожий на флаг, менший консолідаційний трикутник</span>
                </div>
                <div class="pt-row">
                    <span><strong>Ascending Triangle</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Пробій горизонтального опору</span>
                    <span>Покупці набирають силу</span>
                </div>
                <div class="pt-row">
                    <span><strong>Descending Triangle</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Пробій горизонтальної підтримки</span>
                    <span>Продавці набирають силу</span>
                </div>
            </div>

            <h6 class="mt-3">🔺 Клинові патерни (обережно!)</h6>
            <div class="pattern-table">
                <div class="pt-row header">
                    <span>Патерн</span><span>Сигнал</span><span>Логіка</span><span></span>
                </div>
                <div class="pt-row">
                    <span><strong>Rising Wedge</strong></span>
                    <span class="bear">Шорт</span>
                    <span>Ціна зростає але діапазон звужується — слабкість биків</span>
                    <span></span>
                </div>
                <div class="pt-row">
                    <span><strong>Falling Wedge</strong></span>
                    <span class="bull">Лонг</span>
                    <span>Ціна падає але діапазон звужується — слабкість ведмедів</span>
                    <span></span>
                </div>
            </div>

            <div class="tip tip-blue">
                📌 <strong>Порядок вивчення графічних патернів:</strong><br/>
                Double Top/Bottom → Head &amp; Shoulders → Flags → Triangles → Wedges → Cup &amp; Handle → Pennants
            </div>
        </div>
        {/if}
    </div>

    <!-- 5. Індикатори -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'indicators'} onclick={() => toggle('indicators')}>
            <span class="step-badge">5</span>
            Індикатори — RSI, EMA, ATR
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'indicators'}
        <div class="guide-body">

            <div class="indicator-block">
                <div class="ind-title" style="color:#e53935">RSI (Relative Strength Index) — індекс відносної сили</div>
                <p>Показує, наскільки <em>перекуплений чи перепроданий</em> актив. Значення від 0 до 100.</p>
                <div class="rsi-scale">
                    <div class="rsi-zone bear">Перепроданий (0–30) → потенційний лонг</div>
                    <div class="rsi-zone neutral">Нейтральний (30–70) → без сигналу</div>
                    <div class="rsi-zone bull">Перекуплений (70–100) → потенційний шорт</div>
                </div>
                <ul class="mt-2">
                    <li>RSI &lt; 30 на 4h + бичачий свічковий патерн = сильний сигнал лонгу</li>
                    <li>RSI &gt; 70 на 4h + ведмежий патерн = сильний сигнал шорту</li>
                    <li><strong>Дивергенція RSI</strong> — ціна робить новий мінімум, а RSI ні → розворот вгору</li>
                </ul>
            </div>

            <div class="indicator-block">
                <div class="ind-title" style="color:#1565c0">EMA (Exponential Moving Average) — експоненційна ковзна середня</div>
                <p>Показує середню ціну за N свічок. Швидко реагує на зміни ціни.</p>
                <div class="ema-legend">
                    <span class="ema-dot" style="background:#2196f3"></span> EMA 9 — дуже короткострокова, реагує першою
                    <span class="ema-dot" style="background:#ff9800; margin-left:12px"></span> EMA 21 — короткострокова
                    <span class="ema-dot" style="background:#9c27b0; margin-left:12px"></span> EMA 200 — довгострокова, "розподіл ринку"
                </div>
                <ul class="mt-2">
                    <li><strong>Ціна вище EMA 200</strong> → бичачий ринок, надавайте перевагу лонгам</li>
                    <li><strong>Ціна нижче EMA 200</strong> → ведмежий ринок, надавайте перевагу шортам</li>
                    <li><strong>EMA 9 перетинає EMA 21 знизу вгору</strong> → сигнал до лонгу (Golden Cross)</li>
                    <li><strong>EMA 9 перетинає EMA 21 зверху вниз</strong> → сигнал до шорту (Death Cross)</li>
                    <li>EMA також виступають рівнями підтримки/опору — ціна часто відбивається від них</li>
                </ul>
            </div>

            <div class="indicator-block">
                <div class="ind-title" style="color:#2e7d32">ATR (Average True Range) — середній справжній діапазон</div>
                <p>Показує середню <em>волатильність</em> за останні 14 свічок. Не показує напрям — тільки розмах руху.</p>
                <ul>
                    <li><strong>Стоп-лос = ATR × 1.5</strong> від точки входу — статистично обґрунтований стоп</li>
                    <li><strong>Тейк-профіт = ATR × 3</strong> — мінімальне співвідношення ризику до прибутку 1:2</li>
                    <li>Великий ATR = висока волатильність → менший розмір позиції</li>
                    <li>Маленький ATR = тихий ринок → може передувати різкому руху (накопичення)</li>
                </ul>
                <div class="tip tip-green">
                    💡 Приклад: BTC, ATR = 800$. Стоп = -1200$ від входу, тейк = +2400$.
                    При депозиті 1000$ ризикуйте не більше 2% = 20$. Розмір позиції: 20$ / 1200$ × ціна BTC.
                </div>
            </div>
        </div>
        {/if}
    </div>

    <!-- 6. Funding Rate & L/S -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'funding'} onclick={() => toggle('funding')}>
            <span class="step-badge">6</span>
            Funding Rate та Long/Short Ratio
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'funding'}
        <div class="guide-body">
            <div class="indicator-block">
                <div class="ind-title">Funding Rate (FR)</div>
                <p>На ф'ючерсних ринках кожні 8 годин трейдери платять один одному залежно від перекосу позицій.</p>
                <div class="pattern-table">
                    <div class="pt-row header"><span>Значення FR</span><span>Що означає</span><span>Дія</span></div>
                    <div class="pt-row">
                        <span class="bear">&gt; +0.05%</span>
                        <span>Занадто багато лонгів, лонгери платять шортерам</span>
                        <span>Обережно з лонгом, ринок перегрітий</span>
                    </div>
                    <div class="pt-row">
                        <span class="neutral">0% ± 0.01%</span>
                        <span>Баланс — ринок нейтральний</span>
                        <span>Нейтрально</span>
                    </div>
                    <div class="pt-row">
                        <span class="bull">&lt; -0.01%</span>
                        <span>Занадто багато шортів, шортери платять лонгерам</span>
                        <span>Можливий short squeeze — обережно з шортом</span>
                    </div>
                </div>
            </div>

            <div class="indicator-block">
                <div class="ind-title">Long/Short Ratio</div>
                <p>Показує співвідношення кількості відкритих лонгів до шортів серед всіх трейдерів.</p>
                <ul>
                    <li><strong>L/S &gt; 2.0</strong> — більшість тримає лонги. Ринок може розвернути вниз (більшість помиляється)</li>
                    <li><strong>L/S ≈ 1.0</strong> — рівновага</li>
                    <li><strong>L/S &lt; 0.7</strong> — переважають шорти, можливий short squeeze вгору</li>
                    <li>Використовуйте <em>протилежно до натовпу</em> — якщо всі в лонгу, ринок може впасти</li>
                </ul>
            </div>

            <div class="tip tip-yellow">
                ⚠️ FR і L/S — це допоміжні сигнали. Вони допомагають <strong>уникати входів</strong> у перегрітий ринок, а не самостійно визначати напрям.
            </div>
        </div>
        {/if}
    </div>

    <!-- 7. Конфлюенс -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'confluence'} onclick={() => toggle('confluence')}>
            <span class="step-badge">7</span>
            Система оцінки сигналів — конфлюенс
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'confluence'}
        <div class="guide-body">
            <p>Не відкривайте позицію за одним сигналом. Використовуйте систему набору балів:</p>

            <div class="confluence-grid">
                <div class="conf-item"><span class="conf-score">+2</span> Тренд на 1d підтверджує напрям (ціна вище/нижче EMA200)</div>
                <div class="conf-item"><span class="conf-score">+2</span> Графічний патерн підтверджено (H&S, флаг, трикутник)</div>
                <div class="conf-item"><span class="conf-score">+2</span> RSI в зоні перекупленості/перепроданості на 4h</div>
                <div class="conf-item"><span class="conf-score">+1</span> Свічковий патерн розвороту на рівні</div>
                <div class="conf-item"><span class="conf-score">+1</span> EMA crossover (9/21) в потрібному напрямку</div>
                <div class="conf-item"><span class="conf-score">+1</span> Funding Rate нейтральний або на вашу користь</div>
                <div class="conf-item"><span class="conf-score">+1</span> L/S Ratio на вашу користь (проти натовпу)</div>
                <div class="conf-item"><span class="conf-score">+1</span> Ключовий рівень підтримки/опору підтверджено</div>
            </div>

            <div class="conf-legend">
                <div class="conf-band bad">0–3 балів — <strong>не входити</strong></div>
                <div class="conf-band warn">4–5 балів — <strong>малий розмір позиції</strong> (0.5–1%)</div>
                <div class="conf-band ok">6–7 балів — <strong>стандартний вхід</strong> (1–2%)</div>
                <div class="conf-band great">8+ балів — <strong>впевнений вхід</strong> (2–3%)</div>
            </div>
        </div>
        {/if}
    </div>

    <!-- 8. Управління ризиком -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'risk'} onclick={() => toggle('risk')}>
            <span class="step-badge">8</span>
            Управління ризиком — найважливіше
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'risk'}
        <div class="guide-body">
            <div class="rules-list">
                <div class="rule">
                    <div class="rule-num">①</div>
                    <div>
                        <strong>Ризик на угоду — максимум 2% депозиту.</strong>
                        Якщо депозит 1000$, максимальний збиток на одній угоді — 20$.
                    </div>
                </div>
                <div class="rule">
                    <div class="rule-num">②</div>
                    <div>
                        <strong>Стоп-лос — обов'язково завжди.</strong>
                        Немає стопу = немає угоди. Виставляйте стоп одразу при відкритті позиції.
                        Формула: <code>Стоп = ціна входу ± ATR × 1.5</code>
                    </div>
                </div>
                <div class="rule">
                    <div class="rule-num">③</div>
                    <div>
                        <strong>Тейк-профіт: мінімум 1:2 (ризик:прибуток).</strong>
                        Якщо стоп = 100$, тейк = мінімум 200$. Краще 1:3.
                    </div>
                </div>
                <div class="rule">
                    <div class="rule-num">④</div>
                    <div>
                        <strong>Ніколи не усереднюйте збиткову позицію.</strong>
                        "Додати до збиткової" = збільшити збиток. Якщо помилилися — закрийте і переосмисліть.
                    </div>
                </div>
                <div class="rule">
                    <div class="rule-num">⑤</div>
                    <div>
                        <strong>Плече — не більше 5–10x для початківців.</strong>
                        Більше плеча = менший стоп до ліквідації. При 20x достатньо руху 5% проти вас.
                    </div>
                </div>
                <div class="rule">
                    <div class="rule-num">⑥</div>
                    <div>
                        <strong>3 збитки поспіль = пауза на день.</strong>
                        Емоційний стан впливає на рішення. Зупиніться, проаналізуйте помилки.
                    </div>
                </div>
            </div>
        </div>
        {/if}
    </div>

    <!-- 9. Покрокова схема для угоди -->
    <div class="guide-section">
        <button class="guide-toggle" class:open={openSection === 'workflow'} onclick={() => toggle('workflow')}>
            <span class="step-badge">9</span>
            Покроковий алгоритм перед кожною угодою
            <svg class="chevron" viewBox="0 0 16 16"><path d="M4 6l4 4 4-4"/></svg>
        </button>
        {#if openSection === 'workflow'}
        <div class="guide-body">
            <div class="workflow">
                <div class="wf-step">
                    <div class="wf-icon">1</div>
                    <div>
                        <strong>Відкрийте 1d графік</strong><br/>
                        Де ціна відносно EMA 200? Який загальний тренд? Є ключові рівні поруч?
                    </div>
                </div>
                <div class="wf-arrow">↓</div>
                <div class="wf-step">
                    <div class="wf-icon">2</div>
                    <div>
                        <strong>Перейдіть на 4h</strong><br/>
                        RSI перекуплений/перепроданий? Є графічний патерн? EMA crossover?
                    </div>
                </div>
                <div class="wf-arrow">↓</div>
                <div class="wf-step">
                    <div class="wf-icon">3</div>
                    <div>
                        <strong>Перейдіть на 30m/15m</strong><br/>
                        Підтверджуючий свічковий патерн? Точна точка входу?
                    </div>
                </div>
                <div class="wf-arrow">↓</div>
                <div class="wf-step">
                    <div class="wf-icon">4</div>
                    <div>
                        <strong>Перевірте Funding Rate і L/S</strong><br/>
                        Ринок не перегрітий? Немає екстремального дисбалансу?
                    </div>
                </div>
                <div class="wf-arrow">↓</div>
                <div class="wf-step">
                    <div class="wf-icon">5</div>
                    <div>
                        <strong>Порахуйте бали конфлюенсу</strong><br/>
                        Якщо менше 5 — не входити.
                    </div>
                </div>
                <div class="wf-arrow">↓</div>
                <div class="wf-step">
                    <div class="wf-icon">6</div>
                    <div>
                        <strong>Розрахуйте розмір позиції</strong><br/>
                        Ризик 2% депо / відстань до стопу (в %) = розмір позиції.
                    </div>
                </div>
                <div class="wf-arrow">↓</div>
                <div class="wf-step wf-final">
                    <div class="wf-icon">✓</div>
                    <div>
                        <strong>Відкрийте позицію зі стопом і тейком</strong><br/>
                        Після відкриття — не дивіться на графік кожну хвилину. Дайте угоді час.
                    </div>
                </div>
            </div>
        </div>
        {/if}
    </div>

</div>

<style>
    .guide {
        max-width: 900px;
        margin: 0 auto;
        font-size: 0.92rem;
    }

    .guide-header {
        margin-bottom: 1.5rem;
    }

    .guide-section {
        border: 1px solid #dee2e6;
        border-radius: 8px;
        margin-bottom: 10px;
        overflow: hidden;
    }

    .guide-toggle {
        width: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        padding: 12px 16px;
        background: #f8f9fa;
        border: none;
        text-align: left;
        font-size: 0.95rem;
        font-weight: 600;
        cursor: pointer;
        transition: background 0.15s;
    }
    .guide-toggle:hover { background: #e9ecef; }
    .guide-toggle.open { background: #e3f2fd; }

    .step-badge {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        background: #1976d2;
        color: #fff;
        border-radius: 50%;
        font-size: 0.75rem;
        font-weight: 700;
        flex-shrink: 0;
    }

    .chevron {
        width: 16px;
        height: 16px;
        margin-left: auto;
        stroke: #666;
        stroke-width: 2;
        fill: none;
        stroke-linecap: round;
        stroke-linejoin: round;
        transition: transform 0.2s;
    }
    .guide-toggle.open .chevron { transform: rotate(180deg); }

    .guide-body {
        padding: 16px;
        background: #fff;
    }

    /* Таймфрейми */
    .tf-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
        margin-bottom: 12px;
    }
    .tf-card {
        border-radius: 6px;
        padding: 12px;
        font-size: 0.85rem;
    }
    .tf-macro { background: #e8f5e9; border: 1px solid #a5d6a7; }
    .tf-mid   { background: #e3f2fd; border: 1px solid #90caf9; }
    .tf-micro { background: #fff8e1; border: 1px solid #ffe082; }
    .tf-label { font-weight: 700; font-size: 0.8rem; margin-bottom: 4px; }
    .tf-values { font-size: 1rem; font-weight: 600; margin-bottom: 6px; color: #333; }

    /* Таблиця патернів */
    .pattern-table { margin-bottom: 8px; }
    .pt-row {
        display: grid;
        grid-template-columns: 1.4fr 0.8fr 1.8fr 0.8fr;
        gap: 4px;
        padding: 5px 8px;
        border-bottom: 1px solid #f0f0f0;
        align-items: center;
    }
    .pt-row.header {
        background: #f5f5f5;
        font-weight: 600;
        font-size: 0.78rem;
        color: #666;
        border-radius: 4px 4px 0 0;
    }
    .bull    { color: #2e7d32; font-weight: 600; }
    .bear    { color: #c62828; font-weight: 600; }
    .neutral { color: #546e7a; }

    /* Індикатори */
    .indicator-block {
        background: #f8f9fa;
        border-radius: 6px;
        padding: 14px;
        margin-bottom: 12px;
    }
    .ind-title {
        font-weight: 700;
        font-size: 0.95rem;
        margin-bottom: 8px;
    }
    .rsi-scale {
        display: grid;
        grid-template-columns: 1fr 1fr 1fr;
        gap: 4px;
        margin: 8px 0;
    }
    .rsi-zone {
        padding: 6px 8px;
        border-radius: 4px;
        font-size: 0.8rem;
        text-align: center;
    }
    .rsi-zone.bear    { background: #e8f5e9; color: #2e7d32; }
    .rsi-zone.neutral { background: #f5f5f5; color: #546e7a; }
    .rsi-zone.bull    { background: #ffebee; color: #c62828; }

    .ema-legend {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 4px;
        font-size: 0.85rem;
        background: #fff;
        border-radius: 4px;
        padding: 8px;
    }
    .ema-dot {
        display: inline-block;
        width: 12px;
        height: 12px;
        border-radius: 50%;
        flex-shrink: 0;
    }

    /* Конфлюенс */
    .confluence-grid {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 6px;
        margin-bottom: 12px;
    }
    .conf-item {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 10px;
        background: #f8f9fa;
        border-radius: 6px;
        font-size: 0.85rem;
    }
    .conf-score {
        font-weight: 700;
        color: #1976d2;
        font-size: 0.95rem;
        flex-shrink: 0;
        width: 28px;
    }
    .conf-legend { display: flex; flex-direction: column; gap: 4px; }
    .conf-band {
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 0.85rem;
    }
    .conf-band.bad   { background: #ffebee; color: #c62828; }
    .conf-band.warn  { background: #fff8e1; color: #f57f17; }
    .conf-band.ok    { background: #e8f5e9; color: #2e7d32; }
    .conf-band.great { background: #e3f2fd; color: #1565c0; }

    /* Ризик */
    .rules-list { display: flex; flex-direction: column; gap: 12px; }
    .rule { display: flex; gap: 12px; align-items: flex-start; }
    .rule-num {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        background: #1976d2;
        color: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.85rem;
    }
    code {
        background: #f0f0f0;
        padding: 1px 5px;
        border-radius: 3px;
        font-size: 0.85em;
    }

    /* Workflow */
    .workflow { display: flex; flex-direction: column; align-items: flex-start; }
    .wf-step {
        display: flex;
        gap: 12px;
        background: #f8f9fa;
        border-radius: 8px;
        padding: 12px;
        width: 100%;
    }
    .wf-final { background: #e8f5e9; }
    .wf-arrow { padding: 2px 0 2px 18px; color: #90a4ae; font-size: 1.2rem; }
    .wf-icon {
        flex-shrink: 0;
        width: 28px;
        height: 28px;
        background: #1976d2;
        color: #fff;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.85rem;
        font-weight: 700;
    }
    .wf-final .wf-icon { background: #2e7d32; }

    /* Підказки */
    .tip {
        padding: 10px 12px;
        border-radius: 6px;
        margin-top: 10px;
        font-size: 0.87rem;
    }
    .tip-green  { background: #e8f5e9; border-left: 3px solid #2e7d32; }
    .tip-blue   { background: #e3f2fd; border-left: 3px solid #1565c0; }
    .tip-yellow { background: #fff8e1; border-left: 3px solid #f57f17; }

    .steps-list { padding-left: 1.2rem; line-height: 2; }

    @media (max-width: 600px) {
        .tf-grid          { grid-template-columns: 1fr; }
        .confluence-grid  { grid-template-columns: 1fr; }
        .pt-row           { grid-template-columns: 1fr 1fr; }
        .pt-row.header span:nth-child(3),
        .pt-row span:nth-child(3) { display: none; }
        .rsi-scale        { grid-template-columns: 1fr; }
    }
</style>
